import { DIYLoadServiceActionsWithFieldsQuery, DIYLoadServiceQueriesWithFieldsQuery, DIYLoadServiceTriggersWithFieldsQuery } from "../queries"
import { graphQueryBang } from "shared/lib/graphql"
import { createStepFromTemplate } from "./template_parser"
import { Service } from "./interfaces"

interface TemplateRequest {
  template: any
  service: Service
  query: any
  variables: any
  type: 'trigger' | 'query' | 'action'
  originalIndex?: number
}

/**
 * Loads template data for DIY Composer by fetching service-specific triggers, queries, and actions.
 *
 * @param {Array<Service>} services - List of available services.
 * @param {any} template - The template containing trigger, queries, and actions.
 * @param {function} dispatch - Dispatch function to update the state.
 */
export const loadTemplateData = async (services: Array<Service>, template: any, dispatch: any) => {
  try {
    const requests: TemplateRequest[] = []

    // Collect all template requests
    if (template.trigger) {
      const [triggerServiceName] = template.trigger.full_module_name.split('.')
      const triggerService = services.find(s => s.module_name === triggerServiceName)
      if (triggerService) {
        requests.push({
          template: template.trigger,
          service: triggerService,
          query: DIYLoadServiceTriggersWithFieldsQuery,
          variables: {
            serviceModuleName: triggerServiceName,
            triggerModuleName: template.trigger.full_module_name
          },
          type: 'trigger'
        })
      }
    }

    if (template.queries?.length > 0) {
      for (let i = 0; i < template.queries.length; i++) {
        const queryTemplate = template.queries[i]
        const [queryServiceName] = queryTemplate.full_module_name.split('.')
        const queryService = services.find(s => s.module_name === queryServiceName)
        if (queryService) {
          requests.push({
            template: queryTemplate,
            service: queryService,
            query: DIYLoadServiceQueriesWithFieldsQuery,
            variables: {
              serviceModuleName: queryServiceName,
              queryModuleName: queryTemplate.full_module_name
            },
            type: 'query',
            originalIndex: i
          })
        }
      }
    }

    if (template.actions?.length > 0) {
      for (let i = 0; i < template.actions.length; i++) {
        const actionTemplate = template.actions[i]
        const [actionServiceName] = actionTemplate.full_module_name.split('.')
        const actionService = services.find(s => s.module_name === actionServiceName)
        if (actionService) {
          requests.push({
            template: actionTemplate,
            service: actionService,
            query: DIYLoadServiceActionsWithFieldsQuery,
            variables: {
              serviceModuleName: actionServiceName,
              actionModuleName: actionTemplate.full_module_name
            },
            type: 'action',
            originalIndex: i
          })
        }
      }
    }

    // Execute all requests concurrently and process responses as they complete
    const templateSteps: any = {}
    const queries: any[] = []
    const actions: any[] = []
    let completedRequests = 0

    const processResponse = (request: TemplateRequest, response: any, success: boolean) => {
      if (success && response) {
        const { channel, [request.type]: stepData } = response
        const updatedService = { ...request.service, ...channel }
        const step = createStepFromTemplate(request.template, updatedService, request.type, stepData)

        if (step) {
          if (request.type === 'trigger') {
            templateSteps.selectedTrigger = step
          } else if (request.type === 'query') {
            queries.push({ step, originalIndex: request.originalIndex })
          } else if (request.type === 'action') {
            actions.push({ step, originalIndex: request.originalIndex })
          }
        }
      }

      completedRequests++

      // Once all requests are completed, apply trigger dependency logic and dispatch
      if (completedRequests === requests.length) {
        const hasTrigger = !!templateSteps.selectedTrigger

        if (queries.length > 0) {
          templateSteps.selectedQueries = queries
            .sort((a, b) => a.originalIndex - b.originalIndex)
            .map(item => ({
              ...item.step,
              disabled: !hasTrigger
            }))
        }
        if (actions.length > 0) {
          templateSteps.selectedActions = actions
            .sort((a, b) => a.originalIndex - b.originalIndex)
            .map(item => ({
              ...item.step,
              disabled: !hasTrigger
            }))
        }

        if (Object.keys(templateSteps).length > 0) {
          dispatch({
            type: "SET_OVERRIDE_STATE",
            value: templateSteps
          })
        }
      }
    }

    requests.forEach(async (request) => {
      try {
        const response = await graphQueryBang(request.query, request.variables)
        processResponse(request, response, true)
      } catch (error) {
        console.error(`Error loading ${request.type} template:`, error)
        processResponse(request, null, false)
      }
    })
  } catch (error) {
    console.error('Error loading template data:', error)
  }
}
